import { IReactionDisposer, makeAutoObservable, reaction, runInAction } from 'mobx';
import axios, { Canceler } from 'axios';

import apiManage, { apiManage_rbp } from '../../request';
import { DefaultWebsocketClient } from '../../modules/websocket/module';
import i18n from '../../i18n';
import {
    getRestaurantVersionCheckBlockI18n,
    getShellAppPathname,
    mapErrorToI18nKey,
    shouldEnforceCheckPermissionDbVersionOnShellPath,
} from '../../constants/tool-tunnel-config';
import { getRBPStringId } from './main-page-states';

/** 与 `HookDecorator` 展示态一致：0 检查中；1 通过；-1 隧道/版本接口错误；-2 无餐厅；-3 版本过低 */
export type TunnelShellPassStatus = 0 | 1 | -1 | -2 | -3;

/**
 * 全局隧道与云 RBP 版本门禁状态（对齐 shell2-app `TunnelStatusStore`）。
 * 仅随当前聚焦餐厅变化触发 `check-permission` / `version-check`，Tab 切换复用缓存结果。
 */
export class TunnelStatusState {
    /** 最近一次校验对应的餐厅 `string_id` */
    currentStringId = '';

    /** 隧道门禁展示状态 */
    passStatus: TunnelShellPassStatus = 0;

    /** `passStatus === -1` 时已本地化的错误句 */
    tunnelFailLine = '';

    /** `passStatus === -3` 时版本过低完整提示 */
    dbVersionGateMessage = '';

    /** 请求序号，用于丢弃换店后的过期响应 */
    requestSeq = 0;

    /** 进行中的 axios 取消函数 */
    private canceler: Canceler | null = null;

    /** 监听 `getRBPStringId()` 的 reaction */
    private watchDisposer: IReactionDisposer | null = null;

    /** `CURRENT_RESTAURANT` 事件回调引用，便于卸载 */
    private onRestaurantChange: (() => void) | null = null;

    constructor() {
        makeAutoObservable(this);
    }

    /** 当前餐厅隧道是否已通过（含版本门禁） */
    get isAvailable(): boolean {
        return this.passStatus === 1 && !!this.currentStringId;
    }

    /**
     * 指定餐厅是否与全局缓存一致且隧道已就绪。
     * @param stringId - 页面使用的餐厅 `string_id`
     */
    isAvailableForRestaurant(stringId: string): boolean {
        return !!stringId && this.currentStringId === stringId && this.passStatus === 1;
    }

    /**
     * 启动全局监听：跟随聚焦餐厅与 `CURRENT_RESTAURANT` 事件同步隧道；重复调用忽略。
     */
    startWatch(): void {
        if (this.watchDisposer) {
            return;
        }

        this.watchDisposer = reaction(
            () => getRBPStringId(),
            (stringId) => {
                void this.syncCurrentRestaurant(stringId);
            },
            { fireImmediately: true },
        );

        this.onRestaurantChange = () => {
            void this.syncCurrentRestaurant(getRBPStringId());
        };
        DefaultWebsocketClient.on('CURRENT_RESTAURANT', this.onRestaurantChange!);
    }

    /** 停止监听并取消进行中的校验请求 */
    stopWatch(): void {
        this.watchDisposer?.();
        this.watchDisposer = null;
        if (this.onRestaurantChange) {
            DefaultWebsocketClient.off('CURRENT_RESTAURANT', this.onRestaurantChange);
            this.onRestaurantChange = null;
        }
        this.cancelInFlightRequest();
    }

    /**
     * 按餐厅刷新隧道与（云 RBP 路径下）版本门禁；同一餐厅且已通过时跳过重复请求。
     * @param stringId - 目标餐厅 `string_id`
     */
    async syncCurrentRestaurant(stringId: string): Promise<void> {
        const sid = (stringId && stringId.trim()) || '';
        const seq = this.requestSeq + 1;
        this.requestSeq = seq;
        this.cancelInFlightRequest();

        if (!sid) {
            runInAction(() => {
                this.currentStringId = '';
                this.passStatus = -2;
                this.tunnelFailLine = '';
                this.dbVersionGateMessage = '';
            });
            return;
        }

        if (this.currentStringId === sid && this.passStatus === 1) {
            return;
        }

        runInAction(() => {
            this.currentStringId = sid;
            this.passStatus = 0;
            this.tunnelFailLine = '';
            this.dbVersionGateMessage = '';
        });

        const cancelToken = new axios.CancelToken((c) => {
            this.canceler = c;
        });

        try {
            const res = await apiManage_rbp.checkPermission({ cancelToken });
            if (this.requestSeq !== seq) {
                return;
            }

            if (!res || res?.error) {
                runInAction(() => {
                    this.passStatus = -1;
                    this.tunnelFailLine = String(i18n.t(mapErrorToI18nKey(res)) ?? '');
                    this.dbVersionGateMessage = '';
                });
                return;
            }

            const pathname = getShellAppPathname();
            if (shouldEnforceCheckPermissionDbVersionOnShellPath(pathname)) {
                const vres = await apiManage.get_restaurant_version_check(sid, { cancelToken });
                if (this.requestSeq !== seq) {
                    return;
                }
                if (vres && vres.error) {
                    runInAction(() => {
                        this.passStatus = -1;
                        this.tunnelFailLine = String(i18n.t(mapErrorToI18nKey(vres)) ?? '');
                        this.dbVersionGateMessage = '';
                    });
                    return;
                }
                const versionBlock = getRestaurantVersionCheckBlockI18n(vres);
                if (versionBlock) {
                    runInAction(() => {
                        this.passStatus = -3;
                        this.dbVersionGateMessage = String(
                            i18n.t(versionBlock.key, versionBlock.params) ?? '',
                        );
                        this.tunnelFailLine = '';
                    });
                    return;
                }
            }

            runInAction(() => {
                this.passStatus = 1;
                this.tunnelFailLine = '';
                this.dbVersionGateMessage = '';
            });
        } catch (e: unknown) {
            if (this.requestSeq !== seq) {
                return;
            }
            if (axios.isCancel(e)) {
                return;
            }
            runInAction(() => {
                this.passStatus = -1;
                this.tunnelFailLine = i18n.t('business_analysis_tunnel_config_error_connection');
                this.dbVersionGateMessage = '';
            });
        } finally {
            if (this.requestSeq === seq) {
                this.canceler = null;
            }
        }
    }

    /** 取消尚未完成的隧道/版本校验请求 */
    private cancelInFlightRequest(): void {
        this.canceler?.();
        this.canceler = null;
    }
}

/** 全局单例，在 Main 布局 `init` 时 `startWatch()` */
export const tunnelStatusStore = new TunnelStatusState();
